import React, { useState, useMemo } from "react";
import { Button, Form, Row, Col, Pagination, Alert, Spinner } from "react-bootstrap";
import { TbFileTime } from "react-icons/tb";
import PayrollLayout from "../../components/PayrollLayout.jsx";
import { getPayrollPeriod, getRecentPayrollPeriods } from "../../utils/payrollPeriod";

const API_URL = "https://backend.ems.uk.hiradentallab.com/api/payroll";
const TIMEZONE = "Europe/London";

/* ---------- Helpers ---------- */

// "Mon 26 Oct 2026" in UK time
const formatUKDate = (date) =>
  new Date(date).toLocaleDateString("en-GB", {
    timeZone: TIMEZONE,
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

// Working days in the period (Sundays excluded) – same rule as before
const countWorkingDays = (start, end) => {
  // Walk the days in UK time
  const toUKParts = (d) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(d);
    const get = (t) => Number(parts.find((p) => p.type === t).value);
    return { y: get("year"), m: get("month") - 1, d: get("day") };
  };
  const s = toUKParts(start);
  const e = toUKParts(end);
  const cursor = new Date(Date.UTC(s.y, s.m, s.d));
  const last = new Date(Date.UTC(e.y, e.m, e.d));

  let count = 0;
  while (cursor <= last) {
    if (cursor.getUTCDay() !== 0) count++;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return count;
};

// The backend expects the month/year in which the period ENDS (the 25th).
// The middle of the period is always in that month.
const getEndMonthYear = (period) => {
  const middle = new Date(new Date(period.startDate).getTime() + 15 * 24 * 60 * 60 * 1000);
  return { month: middle.getMonth() + 1, year: middle.getFullYear() };
};

const PayrollReport = () => {
  // ===== Period is picked automatically (current payroll period) =====
  const [availablePeriods] = useState(() => getRecentPayrollPeriods(6, 1));
  const [selectedPeriod, setSelectedPeriod] = useState(() => getPayrollPeriod());

  const [payrollData, setPayrollData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [includeUnapprovedOvertime, setIncludeUnapprovedOvertime] = useState(false);
  const itemsPerPage = 10;

  const workingDays = useMemo(
    () => countWorkingDays(selectedPeriod.startDate, selectedPeriod.endDate),
    [selectedPeriod]
  );

  const handlePeriodChange = (e) => {
    const p = availablePeriods.find((x) => x.label === e.target.value);
    if (p) {
      setSelectedPeriod(p);
      setShowTable(false);
      setAlert(null);
    }
  };

  /* ---------- Generate payroll via API ---------- */
  const handleGenerateReport = async () => {
    const { month, year } = getEndMonthYear(selectedPeriod);

    setLoading(true);
    setAlert(null);

    try {
      const res = await fetch(`${API_URL}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ month, year, includeUnapprovedOvertime }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to generate payroll");
      }

      setPayrollData(data.data || []);
      setShowTable(true);
      setCurrentPage(1);

      setAlert({
        variant: data.errors && data.errors.length > 0 ? "warning" : "success",
        message: data.message,
        errors: data.errors,
      });
    } catch (err) {
      setAlert({ variant: "danger", message: err.message });
      setShowTable(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleGenerateReport();
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const totalPages = Math.ceil(payrollData.length / itemsPerPage);

  const pageRange = () => {
    const range = [];
    for (let i = 1; i <= totalPages; i++) range.push(i);
    return range;
  };

  const currentData = payrollData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleExportToExcel = () => console.log("Exporting to Excel");
  const handleExportToPDF = () => console.log("Exporting to PDF");

  const calculateTotal = (field) =>
    currentData.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);

  const fmt = (v, d = 2) => (Number(v) || 0).toFixed(d);

  return (
    <PayrollLayout>
      <h1 className="all-report-title">
        <TbFileTime /> Generate Payroll
      </h1>
      <hr />

      <Form className="all-report-form" onSubmit={handleSubmit}>
        <Row>
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label htmlFor="payroll_period">Payroll Period :</Form.Label>
              <Form.Select
                id="payroll_period"
                value={selectedPeriod.label}
                onChange={handlePeriodChange}
              >
                {availablePeriods.map((p) => (
                  <option key={p.label} value={p.label}>
                    {p.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Current period is selected automatically
              </Form.Text>
            </Form.Group>
          </Col>

          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label>Start date :</Form.Label>
              <Form.Control type="text" value={formatUKDate(selectedPeriod.startDate)} readOnly />
            </Form.Group>
          </Col>

          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label>End date :</Form.Label>
              <Form.Control type="text" value={formatUKDate(selectedPeriod.endDate)} readOnly />
            </Form.Group>
          </Col>

          <Col xs={12} md={2}>
            <Form.Group>
              <Form.Label>Working Days :</Form.Label>
              <Form.Control type="number" value={workingDays} readOnly />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col xs={12}>
            <Form.Check
              type="checkbox"
              id="includeUnapprovedOvertime"
              label="Include unapproved overtime in payable hours"
              checked={includeUnapprovedOvertime}
              onChange={(e) => setIncludeUnapprovedOvertime(e.target.checked)}
            />
          </Col>
        </Row>

        <Button
          className="generate-report-button mt-3"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Generating...
            </>
          ) : (
            "Generate Payroll"
          )}
        </Button>

        <div className="text-muted mt-2" style={{ fontSize: "13px" }}>
          Once generated, payroll updates automatically whenever attendance hours change
          (until it is marked as paid).
        </div>
      </Form>

      {alert && (
        <Alert
          variant={alert.variant}
          className="mt-3"
          dismissible
          onClose={() => setAlert(null)}
        >
          {alert.message}
          {alert.errors && alert.errors.length > 0 && (
            <ul className="mb-0 mt-2">
              {alert.errors.map((e, i) => (
                <li key={i}>
                  <strong>{e.userName}</strong> ({e.userId}): {e.error}
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      {showTable && (
        <div className="emps-leave-report-table-container">
          <div className="table-actions">
            <Row className="w-100">
              <Col
                xs={12}
                md={6}
                className="d-flex align-items-center mb-3 justify-content-start"
              >
                <Button
                  variant="secondary"
                  className="export-button"
                  onClick={handleExportToExcel}
                >
                  Excel
                </Button>
                <Button
                  variant="secondary"
                  className="export-button"
                  onClick={handleExportToPDF}
                  style={{ marginLeft: "10px" }}
                >
                  PDF
                </Button>
              </Col>
            </Row>
          </div>

          <div className="table-responsive">
            <table className="table table-striped">
              <thead className="table-header">
                <tr className="emps-payroll-table-header">
                  <th>SR</th>
                  <th>User ID</th>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Monthly Salary</th>
                  <th>Contracted Hrs</th>
                  <th>Present</th>
                  <th>Absent</th>
                  <th>Leave</th>
                  <th>OT Hours</th>
                  <th>Gross Salary</th>
                  <th>Net Salary</th>
                  <th>Period</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((data, index) => (
                  <tr key={data._id || index}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{data.userId}</td>
                    <td>{data.userName}</td>
                    <td>{data.department}</td>
                    <td>{fmt(data.monthlySalary)}</td>
                    <td>{fmt(data.contractedHours)}</td>
                    <td>{data.presentDays}</td>
                    <td>{data.absentDays}</td>
                    <td>{data.leaveDays}</td>
                    <td>{fmt(data.totalOvertimeHours)}</td>
                    <td>{fmt(data.grossSalary)}</td>
                    <td>{fmt(data.netSalary)}</td>
                    <td>{data.periodLabel}</td>
                    <td>{data.status}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-footer">
                <tr className="emps-payroll-table-footer">
                  <td>Total:</td>
                  <td>{currentData.length} employees</td>
                  <td></td>
                  <td></td>
                  <td>{fmt(calculateTotal("monthlySalary"))}</td>
                  <td>{fmt(calculateTotal("contractedHours"))}</td>
                  <td>{calculateTotal("presentDays")}</td>
                  <td>{calculateTotal("absentDays")}</td>
                  <td>{calculateTotal("leaveDays")}</td>
                  <td>{fmt(calculateTotal("totalOvertimeHours"))}</td>
                  <td>{fmt(calculateTotal("grossSalary"))}</td>
                  <td>{fmt(calculateTotal("netSalary"))}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>

            <div className="pagination-container">
              <Row>
                <Col xs={12} md={6} className="d-flex align-items-center">
                  <span>
                    Showing {currentPage} of {totalPages || 1} Pages
                  </span>
                </Col>
                <Col xs={12} md={6} className="d-flex justify-content-end">
                  <Pagination>
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Pagination.Prev>
                    {pageRange().map((page) => (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Pagination.Next>
                  </Pagination>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      )}
    </PayrollLayout>
  );
};

export default PayrollReport;